'use client';

import BackIconBlack from '@/assets/icons/back-black-icon.png';
import BackIconWhite from '@/assets/icons/back-icon.png';
import ShareIconBlack from '@/assets/icons/ShareIconBlack.svg';
import ShareIconWhite from '@/assets/icons/ShareIconWhite2.svg';
import FeedbackSVGBlack from '@/assets/icons/feedbackBlack.svg';
import FeedbackSVGWhite from '@/assets/icons/feedbackWhite.svg';
import { useTranslation } from '@/i18n/client';
import { uaParse } from '@/intl-jdi-common-ssr/utils/uaParser';
import cn from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import './index.scss';
import { getCmsShareText, ItemKetTypeEnum } from '@/apis/cms/apiCms';
import { SourceType } from '@/components/globalFeedbackDrawer/types';
import FeedbackTrigger from '@/components/feedbackTrigger';

interface IDATAStyle {
  // 圆角间距
  radiusMargin?: {
    top?: number; // 上边距
    bottom?: number; // 下边距
    radius?: number; // 圆角
    leftRight?: number; // 左右边距
  };
}
interface UKH5TitleBarProps {
  moduleData: any;
  seo: any;
  customBackButton?: boolean;
  defaultShowTitle?: boolean;
  customBackHref?: string;
  shareClickPoint?: () => void;
}
export default function Module({
  moduleData,
  seo,
  customBackButton = false,
  defaultShowTitle = false,
  customBackHref = '',
  shareClickPoint,
}: UKH5TitleBarProps) {
  const { activityHeaderDarkMode } = seo ?? {};
  const [currentStyle, setCurrentStyle] = useState({
    titleBackgroundColor: 'transparent', // 标题背景色
    currentImage: activityHeaderDarkMode ? BackIconWhite : BackIconBlack, // 当前图片
    nameColor: 'transparent', // 名称颜色
    shareIcon: activityHeaderDarkMode ? <ShareIconWhite /> : <ShareIconBlack />,
    feedbackIcon: activityHeaderDarkMode ? (
      <FeedbackSVGWhite style={{ marginLeft: '12px' }} />
    ) : (
      <FeedbackSVGBlack style={{ marginLeft: '12px' }} />
    ),
  });
  let callTimes = 0; // 名称颜色

  const { t } = useTranslation();
  const router = useRouter();
  const { isInOchamaApp } = uaParse();

  const [isApp, setIsApp] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { dataBatch } = moduleData;

  // -------------- Handle Scroll -------------------- //
  const handleScroll = async () => {
    const scrollY = window.scrollY; // 获取滚动距离
    if (scrollY > 40) {
      setScrolled(true);
      // 超过 40px 的情况
      setCurrentStyle({
        titleBackgroundColor: '#fff',
        currentImage: BackIconBlack, // 滚动时使用的图片
        nameColor: 'black',
        shareIcon: <ShareIconBlack />,
        feedbackIcon: <FeedbackSVGBlack style={{ marginLeft: '12px' }} />,
      });
      callTimes++;
      if (callTimes === 1) {
        const { callNativeAPI } = await import('@/utils/bridge');
        callNativeAPI('updateStatusBarStyle', { style: 'dark' });
      }
    } else if (scrollY > 0) {
      // 在 0 到 40px 之间
      const opacity = scrollY / 40; // 计算透明度
      setCurrentStyle({
        titleBackgroundColor: `rgba(255, 255, 255, ${opacity})`,
        currentImage: activityHeaderDarkMode ? BackIconWhite : BackIconBlack, // 滚动时使用的图片
        shareIcon: activityHeaderDarkMode ? <ShareIconWhite /> : <ShareIconBlack />,
        nameColor: `rgba(0, 0, 0, ${opacity})`,
        feedbackIcon: activityHeaderDarkMode ? (
          <FeedbackSVGWhite style={{ marginLeft: '12px' }} />
        ) : (
          <FeedbackSVGBlack style={{ marginLeft: '12px' }} />
        ),
      });
    } else {
      setScrolled(false);
      // 回到顶部
      setCurrentStyle({
        titleBackgroundColor: 'transparent',
        currentImage: activityHeaderDarkMode ? BackIconWhite : BackIconBlack, // 滚动时使用的图片
        nameColor: defaultShowTitle ? 'black' : 'transparent',
        shareIcon: activityHeaderDarkMode ? <ShareIconWhite /> : <ShareIconBlack />,
        feedbackIcon: activityHeaderDarkMode ? (
          <FeedbackSVGWhite style={{ marginLeft: '12px' }} />
        ) : (
          <FeedbackSVGBlack style={{ marginLeft: '12px' }} />
        ),
      });
      callTimes = 0;
    }
  };
  /**
   * 处理返回事件
   */
  const backHandler = async () => {
    if (isApp) {
      const bridgeModule = await import('@/utils/bridge');
      const JSBridge = bridgeModule.JSBridge;
      JSBridge.functionInApp({
        method: 'back',
      });
    } else if (isInOchamaApp) {
      if (document?.referrer) {
        router.back();
      } else {
        location.href = '/';
      }
    } else {
      router.back();
      return;
    }
  };

  useEffect(() => {
    const { isInJoybuyApp } = uaParse();
    if (isInJoybuyApp) {
      setIsApp(true);
    }
  }, [setIsApp]);

  const beforeDestroy = () => {
    window.removeEventListener('scroll', handleScroll); // 清理事件监听
  };

  /**
   * 分享网页到应用
   * @param 无
   * @returns 无
   */

  const timer = useRef<any>(null);
  const shareToApp = (() => {
    return async () => {
      if (timer.current) return null;
      timer.current = setTimeout(() => {
        timer.current = null;
      }, 1000);
      try {
        let shareTitle = '';
        if (seo?.shareGuidingText && seo?.shareGuidingText.length > 0) {
          shareTitle = seo.shareGuidingText + ' ';
        } else {
          try {
            const res = await getCmsShareText(ItemKetTypeEnum.TEXT, 'cms.campaign.share.text');
            // 根据实际API响应结构取值，假设为 res?.data?.[0]?.value
            shareTitle =
              res?.data && res.data?.configList && res.data?.configList.length > 0
                ? res.data?.configList[0]?.value
                : `${t('share.text')}`;
          } catch {
            shareTitle = `${t('share.text')}`;
          }
        }
        const bridgeModule = await import('@/utils/bridge');
        const JSBridge = bridgeModule.JSBridge;
        JSBridge.functionInApp({
          method: 'shareWebPage',
          payload: {
            shareTitle: shareTitle,
            shareLink: window.location.href,
          },
        });
        shareClickPoint?.();
      } catch (e) {
        clearTimeout(timer.current);
        timer.current = null;
        throw e;
      }
    };
  })();

  const getTitleStyle = () => {
    if (isInOchamaApp) {
      return {
        marginTop: '0px',
      };
    }
    return {};
  };

  const getTitleBarStyle = () => {
    if (isInOchamaApp) {
      return {
        height: '44px',
        marginTop: '0px',
        backgroundColor: currentStyle.titleBackgroundColor,
      };
    }
    return {
      backgroundColor: currentStyle.titleBackgroundColor,
    };
  };

  const getCurrentImage = () => {
    if (isInOchamaApp) {
      return BackIconBlack;
    }
    return currentStyle.currentImage;
  };

  const getNameColor = () => {
    if (isInOchamaApp) {
      return { color: 'black' };
    }
    return { color: currentStyle.nameColor };
  };

  // -------------- UseEffect -------------------- //
  useEffect(() => {
    (async () => {
      const { callNativeAPI } = await import('@/utils/bridge');
      // 初始化告诉原生为light
      callNativeAPI('updateStatusBarStyle', { style: 'light' });
      window.addEventListener('scroll', handleScroll); // 监听滚动事件
    })();
  }, [moduleData]);

  return (
    <div>
      <div className={cn('h5-titlebar', { app: isApp })} style={getTitleBarStyle()}>
        <div className='title' style={getTitleStyle()}>
          <button
            onClick={() => {
              if (customBackButton) {
                location.href = customBackHref;
              } else {
                backHandler();
              }
            }}
          >
            <Image className='img' src={getCurrentImage()} alt='' />
          </button>
          <div className='name' style={getNameColor()}>
            {dataBatch.titleSetting && dataBatch.titleSetting[0] && dataBatch.titleSetting[0].name}
          </div>
          {!isInOchamaApp && (
            <button className='shareIcon' onClick={shareToApp}>
              {currentStyle.shareIcon}
            </button>
          )}
          <FeedbackTrigger sourceType={SourceType.Marketing_Feedback}>
            {currentStyle.feedbackIcon}
          </FeedbackTrigger>
        </div>
      </div>
    </div>
  );
}
